<template>
    <BaseForm v-if="project">
        <template #fields>
            <TextField
                :value="values.nameShort"
                :label="$t('name-short')"
                :error-messages="errors.nameShort"
                :required="true"
                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameShort', true)"
            />
            <TextField
                :value="values.nameLong"
                :label="$t('name-long')"
                :error-messages="errors.nameLong"
                :required="true"
                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('nameLong', true)"
            />
            <TextField
                :value="values.subtitle"
                :label="$t('subtitle')"
                :error-messages="errors.subtitle"
                @input="setFieldValue('subtitle', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('subtitle', true)"
            />
            <NumberField
                :value="values.executionYear ?? ''"
                :label="$t('execution-year')"
                :error-messages="errors.executionYear"
                :required="true"
                @input="setFieldValue('executionYear', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('executionYear', true)"
            />
            <TextAreaField
                :value="values.description"
                :label="$t('description')"
                :max-length="projectIdentifiersSchema.shape.description.maxLength ?? undefined"
                :error-messages="errors.description"
                @input="setFieldValue('description', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('description', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import NumberField from '@/components/Form/NumberField.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useForm } from '@/forms/composables/useForm';
import { useProjectInjection } from '@/project/composables/useProjectInjection';
import { getProjectIdentifiersSchema } from '@/project/schemas';
import { computed } from 'vue';

const props = defineProps<{ projectId: string }>();

const projectId = computed(() => props.projectId);

const projectIdentifiersSchema = computed(() => getProjectIdentifiersSchema(projectId.value));

const { project, updateProject } = useProjectInjection();

const { values, errors, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    projectIdentifiersSchema.value,
    computed(() => ({
        nameShort: project.value?.nameShort ?? '',
        nameLong: project.value?.nameLong ?? '',
        subtitle: project.value?.subtitle ?? '',
        executionYear: project.value?.executionYear ?? '',
        description: project.value?.description ?? '',
    })),
);

async function save() {
    const valid = await validate();
    if (valid) {
        await updateProject({ id: projectId.value, ...values.value });
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
